import React, { useEffect, useState } from "react";
import matrixLogo from "./assets/images/matrixos-logo-transparent.png";
import matrixMark from "./assets/images/matrixos-m-dark-logo.png";
import matrixWordmark from "./assets/images/matrixos-wordmark-logo.png";
import "./MatrixOSEnterPage.css";

const navItems = [
  { label: "Neo", href: "#neo" },
  { label: "Morpheus", href: "#morpheus" },
  { label: "Integrations", href: "#integrations" },
  { label: "Pricing", href: "#pricing" },
];

const nodeDetails = {
  POS: { side: "System of Record", title: "Point of Sale", description: "MatrixOS can consume operational data from the POS you already use — such as sales, product, stock or performance signals — without asking you to replace the till system running the business.", fit: "Feeds trusted transaction and operational signals into the MatrixOS layer." },
  Payroll: { side: "System of Record", title: "Payroll", description: "Payroll remains the financial source of truth. MatrixOS can use workforce and labour data around it to improve visibility, scheduling context and operational accountability.", fit: "Keeps payroll where it belongs while MatrixOS focuses on day-to-day execution." },
  Inventory: { side: "System of Record", title: "Inventory", description: "Existing inventory systems can provide theoretical stock or stock-on-hand. Morpheus adds physical counting, verification, variance, exposure and operational response on top of that data.", fit: "Turns stock records into verified operational intelligence." },
  Delivery: { side: "System of Record", title: "Delivery & Dispatch", description: "Delivery, dispatch and movement systems can remain in place while MatrixOS connects their operational events to the people who need to act on them.", fit: "Connects movement data to execution and response." },
  Accounting: { side: "System of Record", title: "Accounting", description: "Accounting software remains the financial system of record. MatrixOS is designed to sit above it, not compete with it, while surfacing operational information that affects performance.", fit: "Preserves finance workflows while giving operators clearer context." },
  Visibility: { side: "Operational Outcome", title: "Visibility", description: "MatrixOS brings signals from people, stock, duties, sites and connected systems into one operational view so leaders can see what is happening without chasing multiple tools.", fit: "See the operation clearly across every site." },
  Accountability: { side: "Operational Outcome", title: "Accountability", description: "Once MatrixOS knows what was expected and what actually happened, it can show who owns the next action, what remains unresolved and where standards are breaking down.", fit: "Make ownership visible and measurable." },
  Response: { side: "Operational Outcome", title: "Response", description: "Response Intelligence is the action layer: alert the right person, escalate when needed, capture the decision and close the loop instead of simply recording that something went wrong.", fit: "Turn operational signals into action." },
  People: { side: "Operational Outcome", title: "People", description: "Neo connects schedules, actual attendance, duties, exceptions and manager decisions to the real people running each shift — giving operators a live view of workforce execution.", fit: "Connect data to the people responsible for execution." },
  Sites: { side: "Operational Outcome", title: "Sites", description: "MatrixOS is built for multi-site operations. Each location can keep its existing systems while owners and regional managers gain a consistent operational layer across the group.", fit: "One operating view across many locations." },
};

const nodeIcons = {
  POS: "pos", Payroll: "payroll", Inventory: "inventory", Delivery: "delivery", Accounting: "accounting",
  Visibility: "visibility", Accountability: "accountability", Response: "response", People: "people", Sites: "sites",
};

const layerTabs = [
  { id: "neo", label: "Neo", stageLabel: "NEO" },
  { id: "morpheus", label: "Morpheus", stageLabel: "MORPHEUS" },
];

// Customer-facing features derived from the Neo and Morpheus application code.
// Keep this list limited to implemented capabilities; do not publish roadmap items here.
const featureGroups = {
  neo: [
    {
      title: "Attendance & workforce",
      icon: "people",
      description: "Know who is on duty and what actually happened on each shift.",
      items: [
        "Staff clock-in and clock-out",
        "Scheduled versus actual attendance",
        "Late arrivals and missed-shift visibility",
        "Active staff and multi-site oversight",
      ],
    },
    {
      title: "Scheduling & approvals",
      icon: "sites",
      description: "Plan coverage and manage changes without losing the history.",
      items: [
        "Monthly shift scheduling and publishing",
        "Staff schedule exceptions and approvals",
        "Shift-change review and decision history",
        "Schedule history and published exports",
      ],
    },
    {
      title: "Standards & response",
      icon: "accountability",
      description: "Turn everyday expectations into visible, accountable work.",
      items: [
        "Recurring duties with deadlines",
        "Duty ownership and overdue tracking",
        "WhatsApp reminders and escalations*",
        "Manager response and action history",
      ],
    },
    {
      title: "Payroll & reporting",
      icon: "visibility",
      description: "Connect the hours worked to management review and decisions.",
      items: [
        "Worked-hours and payroll review workflows",
        "Overtime and premium classification review",
        "Attendance and operational history reports",
        "Site and workforce management controls",
      ],
    },
  ],
  morpheus: [
    {
      title: "Inventory intake",
      icon: "inventory",
      description: "Get stock data organised before the first count begins.",
      items: [
        "CSV stock-on-hand importing",
        "Product mapping, review and preparation",
        "Site-level inventory distribution",
        "POS SOH sync where connected*",
      ],
    },
    {
      title: "Cycles & physical counts",
      icon: "sites",
      description: "Run structured counts with clear progress and ownership.",
      items: [
        "Named count cycles and sections",
        "Category and count-group configuration",
        "Full-site and section count sessions",
        "Count progress and outstanding sessions",
      ],
    },
    {
      title: "Variances & verification",
      icon: "accountability",
      description: "Separate completing a count from reviewing the result.",
      items: [
        "Count-to-count and archive comparisons",
        "Variance and financial exposure visibility",
        "Separate verification and finalisation",
        "Completed-session records and exports",
      ],
    },
    {
      title: "Inventory intelligence",
      icon: "visibility",
      description: "Find what needs attention across individual and multiple sites.",
      items: [
        "Stock-on-hand views and reporting",
        "Dead-stock identification",
        "Movement evidence across sites",
        "Inventory and stock-count PDF exports",
      ],
    },
  ],
};

const featuredLayers = {
  neo: {
    eyebrow: "NEO  /  WORKFORCE OPERATIONS",
    title: "Your team, their shifts and every duty. All visible.",
    description: "From staff attendance to schedules, recurring duties and manager decisions, Neo brings daily workforce execution into one place.",
    disclaimer: "*WhatsApp delivery depends on account configuration. Payroll review features do not imply that statutory PAYE/UIF payroll is fully live.",
  },
  morpheus: {
    eyebrow: "MORPHEUS  /  INVENTORY INTELLIGENCE",
    title: "Count stock. Investigate variances. See the bigger picture.",
    description: "Prepare inventory, organise physical counts, review differences and monitor stock movement across your locations.",
    disclaimer: "*POS synchronization requires a supported provider, a valid connection and the required access. CSV import is available as an alternative.",
  },
};


const pricingCards = [
  {
    id: "pilot",
    label: "Pilot",
    price: "R0",
    meta: "30-day pilot",
    note: "One site of your choosing · can include Neo and Morpheus",
    items: [
      "Free onboarding support",
      "Four days of guided training",
      "Call support during your pilot",
      "No obligation to continue",
    ],
  },
  {
    id: "neo",
    label: "Neo",
    price: "R699 or R35",
    meta: "Whichever is higher · per month",
    items: [
      "Workforce management",
      "Attendance, scheduling and duties",
      "Exceptions, reports and payroll review",
    ],
  },
  {
    id: "morpheus",
    label: "Morpheus",
    price: "R999",
    meta: "per site / per month",
    items: [
      "Inventory intake and counting",
      "Variance investigation",
      "Reporting and dead-stock insights",
      "POS SOH sync where connected",
    ],
  },
  {
    id: "combined",
    label: "Combined",
    price: "R1498",
    meta: "per site / per month",
    badge: "MOST POPULAR",
    items: [
      "Includes Neo and Morpheus",
      "Complete operational visibility",
      "Better insights, faster response",
      "Simple, transparent pricing",
    ],
  },
];

function NodeIcon({ type }) {
  const common = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
  switch (type) {
    case "pos": return <svg {...common}><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M8 7h8M8 11h3M13 11h3M8 15h8M9 19h6"/></svg>;
    case "payroll": return <svg {...common}><circle cx="12" cy="7" r="3"/><path d="M6 20c.5-4 2.5-6 6-6s5.5 2 6 6M18 5v4M16 7h4"/></svg>;
    case "inventory": return <svg {...common}><path d="m4 7 8-4 8 4-8 4-8-4Z"/><path d="m4 7 8 4 8-4v10l-8 4-8-4V7Z"/><path d="M12 11v10"/></svg>;
    case "delivery": return <svg {...common}><path d="M3 6h11v11H3zM14 10h4l3 3v4h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>;
    case "accounting": return <svg {...common}><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M8 7h8M8 11h2M14 11h2M8 15h2M14 15h2"/></svg>;
    case "visibility": return <svg {...common}><path d="M2.5 12s3.5-5 9.5-5 9.5 5 9.5 5-3.5 5-9.5 5-9.5-5-9.5-5Z"/><circle cx="12" cy="12" r="2.5"/></svg>;
    case "accountability": return <svg {...common}><path d="M12 3 5 6v5c0 4.5 2.8 8 7 10 4.2-2 7-5.5 7-10V6l-7-3Z"/><path d="m9 12 2 2 4-4"/></svg>;
    case "response": return <svg {...common}><path d="M13 2 5 13h6l-1 9 8-12h-6l1-8Z"/></svg>;
    case "people": return <svg {...common}><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6M15 14c3 0 5 1.5 6 4"/></svg>;
    case "sites": return <svg {...common}><path d="M12 21s6-5 6-11a6 6 0 1 0-12 0c0 6 6 11 6 11Z"/><circle cx="12" cy="10" r="2"/></svg>;
    default: return null;
  }
}

export default function MatrixOSEnterPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);
  const [activeNode, setActiveNode] = useState(null);
  const [activeLayer, setActiveLayer] = useState("neo");
  useEffect(() => {
    if (!activeNode) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setActiveNode(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeNode]);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);

    const resetToTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    resetToTop();
    window.requestAnimationFrame(() => {
      resetToTop();
      window.requestAnimationFrame(resetToTop);
    });
    const shortReset = window.setTimeout(resetToTop, 80);
    const finalReset = window.setTimeout(resetToTop, 240);

    return () => {
      window.clearTimeout(shortReset);
      window.clearTimeout(finalReset);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const scrollElementToViewportTop = (element, viewportOffset = 0, behavior = "smooth") => {
    if (!element) return;
    const top = window.scrollY + element.getBoundingClientRect().top - viewportOffset;
    window.scrollTo({ top: Math.max(0, top), left: 0, behavior });
  };

  const settleScroll = (element, viewportOffset = 0) => {
    if (!element) return;
    // Section 2 changes layout when the active layer changes, especially on iPad.
    // Re-apply the exact landing position after React/layout/image sizing has settled.
    scrollElementToViewportTop(element, viewportOffset, "smooth");
    window.setTimeout(() => scrollElementToViewportTop(element, viewportOffset, "auto"), 120);
    window.setTimeout(() => scrollElementToViewportTop(element, viewportOffset, "auto"), 320);
  };

  const selectLayer = (layerId, shouldScroll = true) => {
    setActiveLayer(layerId);
    if (window.history?.replaceState) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
    if (shouldScroll) {
      // Land the layer switcher at the very top of the scrolling viewport.
      // This intentionally scrolls ~one header-height farther than the old landing,
      // matching the approved view where the selected product panel begins immediately below.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          settleScroll(document.querySelector(".mx-layers-tabs"), 0);
        });
      });
    }
  };

  const handlePrimaryNav = (event, item) => {
    if (!item.href?.startsWith("#")) return;
    const target = item.href.slice(1);
    if (layerTabs.some((layer) => layer.id === target)) {
      event.preventDefault();
      selectLayer(target, true);
      closeMenu();
      return;
    }
    if (target === "platform") {
      event.preventDefault();
      const platformSection = document.querySelector("section.mx-home-hero");
      window.requestAnimationFrame(() => settleScroll(platformSection, 0));
      closeMenu();
      return;
    }
    if (target === "story") {
      event.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      closeMenu();
      return;
    }
    if (target === "pricing") {
      event.preventDefault();
      const pricingSection = document.querySelector("section.mx-pricing-shell");
      window.requestAnimationFrame(() => settleScroll(pricingSection, 0));
      closeMenu();
      return;
    }
    if (target === "integrations") {
      event.preventDefault();
      // Direct jump to the actual Section 3 shell. Do not target the similarly named
      // integration-method row inside the hero diagram.
      const integrationsSection = document.querySelector("section.mx-section-three");
      window.requestAnimationFrame(() => {
        settleScroll(integrationsSection, 0);
      });
      closeMenu();
    }
  };

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedSection = params.get("section");
    if (requestedSection === "integrations") {
      const landOnIntegrations = () => {
        const section = document.querySelector("section.mx-section-three");
        if (section) scrollElementToViewportTop(section, 0, "auto");
      };
      const first = window.setTimeout(landOnIntegrations, 320);
      const second = window.setTimeout(landOnIntegrations, 560);
      const clean = window.setTimeout(() => {
        const next = new URLSearchParams(window.location.search);
        next.delete("section");
        const query = next.toString();
        window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
      }, 700);
      return () => {
        window.clearTimeout(first);
        window.clearTimeout(second);
        window.clearTimeout(clean);
      };
    }

    const requestedLayer = params.get("layer");
    if (!requestedLayer || !layerTabs.some((layer) => layer.id === requestedLayer)) return undefined;

    setActiveLayer(requestedLayer);

    const landOnRequestedLayer = () => {
      const tabs = document.querySelector(".mx-layers-tabs");
      if (tabs) scrollElementToViewportTop(tabs, 0, "auto");
    };

    // The homepage deliberately resets to the top on mount. Land on the requested
    // layer only after that reset and the responsive product panel have settled.
    const first = window.setTimeout(landOnRequestedLayer, 320);
    const second = window.setTimeout(landOnRequestedLayer, 560);
    const clean = window.setTimeout(() => {
      const next = new URLSearchParams(window.location.search);
      next.delete("layer");
      const query = next.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }, 700);

    return () => {
      window.clearTimeout(first);
      window.clearTimeout(second);
      window.clearTimeout(clean);
    };
  }, []);

  return (
    <main className="mx-home-shell" id="top">
      <div className="mx-home-ambient mx-home-ambient-one" />
      <div className="mx-home-ambient mx-home-ambient-two" />
      <div className="mx-home-grid" />

      <header className="mx-home-header">
        <div className="mx-home-header-inner">
          <a
            className="mx-home-brand"
            href="/"
            aria-label="MatrixOS home"
            onClick={closeMenu}
          >
            <img src={matrixLogo} alt="MatrixOS" className="mx-home-logo" />
          </a>

          <nav className="mx-home-nav" aria-label="Primary navigation">
            {navItems.map((item) => (
              <a key={item.label} href={item.href} className={item.soon ? "is-coming-soon" : ""} onClick={(event) => handlePrimaryNav(event, item)}>
                <span>{item.label}</span>
                {item.soon && <small>Soon</small>}
              </a>
            ))}
          </nav>

          <div className="mx-home-actions">
            <a className="mx-home-discovery" href="/discovery">
              Book a Discovery
            </a>
            <a className="mx-home-signin" href="/login">
              Sign in
            </a>
            <button
              type="button"
              className={`mx-home-menu-toggle ${menuOpen ? "is-open" : ""}`}
              aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={menuOpen}
              aria-controls="mx-mobile-navigation"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {menuOpen && (
          <button
            type="button"
            className="mx-home-menu-backdrop"
            aria-label="Close navigation menu"
            onClick={closeMenu}
          />
        )}

        <div
          id="mx-mobile-navigation"
          className={`mx-home-mobile-menu ${menuOpen ? "is-open" : ""}`}
        >
          <nav aria-label="Mobile navigation">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={item.soon ? "is-coming-soon" : ""}
                onClick={(event) => handlePrimaryNav(event, item)}
              >
                <span>{item.label}</span>
                {item.soon && <small>Coming Soon</small>}
              </a>
            ))}
            <a className="mx-mobile-discovery" href="/discovery" onClick={closeMenu}>
              Book a Discovery
            </a>
            <a className="mx-mobile-signin" href="/login" onClick={closeMenu}>
              Sign in
            </a>
          </nav>
        </div>
      </header>

      <section className="mx-founder-story" id="story" aria-labelledby="mx-story-title">
        <div className="mx-founder-story-inner">
          <div className="mx-founder-story-heading">
            <span className="mx-founder-story-eyebrow">THE STORY BEHIND MATRIXOS</span>
            <h1 id="mx-story-title">I built it for myself. <strong>Then I realised I wasn't the only one.</strong></h1>
            <p className="mx-founder-story-lead">MatrixOS didn't begin as a product to sell. It began as a way to make a difficult job more manageable — after years of working shifts, counting stock, managing schedules and chasing answers.</p>
            <div className="mx-founder-story-line" aria-hidden="true"><span /></div>
            <span className="mx-founder-story-signature">Built by an operator, from real operations.</span>
            <div className="mx-founder-hero-actions">
              <a className="mx-home-hero-primary" href="/discovery">Book a Discovery <span aria-hidden="true">→</span></a>
              <a className="mx-home-hero-secondary" href="#layers" onClick={(event) => { event.preventDefault(); selectLayer("neo", true); }}>Explore Features <span aria-hidden="true">→</span></a>
            </div>
          </div>
          <div className="mx-founder-story-letter">
            <span className="mx-founder-story-letter-tag">A NOTE FROM THE FOUNDER</span>
            <p>I've worked the shifts, counted stock hour after hour, managed schedules, checked attendance and followed up on duties that should already have been done.</p>
            <p>And even after a long day, the questions continued. Was the count completed? Who came in late? Who followed up on the issue? Why did I have to chase the same answers again?</p>
            <p>So I started building something for myself. Not for investors. Not because I planned to launch a software company. I just wanted to see what was happening in my operation without having to be everywhere or constantly ask.</p>
            <p>Over time, I realised other owners and managers were carrying many of those same frustrations. What started as my own solution grew into <strong>MatrixOS</strong>.</p>
            <div className="mx-founder-story-bottom">
              <span>Visibility</span><i aria-hidden="true">→</i><span>Accountability</span><i aria-hidden="true">→</i><span>Response</span>
            </div>
          </div>
        </div>
      </section>


      <section className="mx-home-hero" id="platform" aria-label="MatrixOS homepage hero">
        <div className="mx-home-hero-copy">
          <span className="mx-home-eyebrow">ONE OPERATIONAL PLATFORM FOR MULTI-SITE BUSINESSES</span>

          <h2 className="mx-home-title">
            <span>Keep your systems.</span>
            <span>Gain <strong>one operational layer.</strong></span>
          </h2>

          <p className="mx-home-subtitle">
            MatrixOS sits above the systems you already use, connects operational data,
            and helps your teams respond across every site.
          </p>

          <div className="mx-home-hero-actions">
            <a className="mx-home-hero-primary" href="/discovery">
              Book a Discovery
            </a>
            <a className="mx-home-hero-secondary" href="#layers" onClick={(event) => { event.preventDefault(); selectLayer("neo", true); }}>
              Explore Features <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        <div className="mx-home-hero-visual">
          <div className="mx-diagram-heading">
            <span>YOUR EXISTING SYSTEMS <small>SYSTEMS OF RECORD</small></span>
            <span>OPERATIONAL OUTCOMES <small>MATRIXOS RESPONSE LAYER</small></span>
          </div>

          <div className="mx-platform-diagram" aria-label="Existing operational systems connect through MatrixOS to operational outcomes">
            <div className="mx-platform-side mx-platform-inputs">
              {["POS", "Payroll", "Inventory", "Delivery", "Accounting"].map((label) => (
                <button key={label} type="button" className={`mx-platform-node ${label === "POS" ? "has-click-hint" : ""}`} onClick={() => setActiveNode(label)} aria-haspopup="dialog">
                  <span className="mx-node-icon"><NodeIcon type={nodeIcons[label]} /></span><strong>{label}</strong>
                  {label === "POS" && <span className="mx-node-click-hint">Click me</span>}
                </button>
              ))}
            </div>

            <div className="mx-platform-lines mx-platform-lines-left" aria-hidden="true">
              <svg className="mx-platform-connector-svg" viewBox="0 0 100 500" preserveAspectRatio="none">
                <path d="M0 50 C42 50, 58 132, 100 158" />
                <path d="M0 150 C46 150, 60 190, 100 205" />
                <path d="M0 250 L100 250" />
                <path d="M0 350 C46 350, 60 310, 100 295" />
                <path d="M0 450 C42 450, 58 368, 100 342" />
                <circle cx="100" cy="158" r="2.6" />
                <circle cx="100" cy="205" r="2.6" />
                <circle cx="100" cy="250" r="2.6" />
                <circle cx="100" cy="295" r="2.6" />
                <circle cx="100" cy="342" r="2.6" />
              </svg>
            </div>

            <div className="mx-platform-core-wrap">
              <span className="mx-platform-orbit mx-platform-orbit-one" aria-hidden="true" />
              <span className="mx-platform-orbit mx-platform-orbit-two" aria-hidden="true" />
              <div className="mx-platform-core">
                <img src={matrixMark} alt="MatrixOS mark" className="mx-platform-core-mark" />
                <img
                  src={matrixWordmark}
                  alt="MatrixOS Operate with Clarity"
                  className="mx-platform-wordmark"
                />
              </div>
            </div>

            <div className="mx-platform-lines mx-platform-lines-right" aria-hidden="true">
              <svg className="mx-platform-connector-svg" viewBox="0 0 100 500" preserveAspectRatio="none">
                <path d="M100 50 C58 50, 42 132, 0 158" />
                <path d="M100 150 C54 150, 40 190, 0 205" />
                <path d="M100 250 L0 250" />
                <path d="M100 350 C54 350, 40 310, 0 295" />
                <path d="M100 450 C58 450, 42 368, 0 342" />
                <circle cx="0" cy="158" r="2.6" />
                <circle cx="0" cy="205" r="2.6" />
                <circle cx="0" cy="250" r="2.6" />
                <circle cx="0" cy="295" r="2.6" />
                <circle cx="0" cy="342" r="2.6" />
              </svg>
            </div>

            <div className="mx-platform-side mx-platform-outcomes">
              {["Visibility", "Accountability", "Response", "People", "Sites"].map((label) => (
                <button key={label} type="button" className="mx-platform-node" onClick={() => setActiveNode(label)} aria-haspopup="dialog">
                  <span className="mx-node-icon"><NodeIcon type={nodeIcons[label]} /></span><strong>{label}</strong>
                </button>
              ))}
            </div>
          </div>

          <div className="mx-integration-identity" id="integration-methods">
            <span className="mx-integration-copy">Built to connect with the systems already running your business.</span>
            <span className="mx-integration-method">API</span>
            <span className="mx-integration-dot" aria-hidden="true">•</span>
            <span className="mx-integration-method">Scheduled Sync</span>
            <span className="mx-integration-dot" aria-hidden="true">•</span>
            <span className="mx-integration-method">Secure Import</span>
          </div>
        </div>
      </section>

      <section className={`mx-layers-shell mx-features-shell is-theme-${activeLayer}`} id="layers" aria-labelledby="mx-layers-title">
        <span id="neo" className="mx-section-anchor" aria-hidden="true" />
        <span id="morpheus" className="mx-section-anchor" aria-hidden="true" />

        <div className="mx-layers-inner">
          <div className="mx-layers-intro">
            <span className="mx-layers-eyebrow">WHAT MATRIXOS OFFERS</span>
            <h2 id="mx-layers-title">Real tools for the work <strong>behind every shift.</strong></h2>
            <p>Explore what Neo and Morpheus can do for your business today. Clear capabilities, practical workflows and a way to try them in your own operation.</p>
          </div>

          <div className="mx-layers-tabs" role="tablist" aria-label="Explore Neo and Morpheus features">
            {layerTabs.map((layer) => (
              <button
                key={layer.id}
                type="button"
                id={`mx-layer-tab-${layer.id}`}
                role="tab"
                aria-selected={activeLayer === layer.id}
                aria-controls="mx-layer-panel"
                className={`mx-layer-tab ${activeLayer === layer.id ? "is-active" : ""}`}
                onClick={() => selectLayer(layer.id, false)}
              >
                <span className="mx-layer-tab-dot" aria-hidden="true" />
                <span>{layer.label}</span>
                <small>{layer.id === "neo" ? "Workforce" : "Inventory"}</small>
              </button>
            ))}
          </div>

          <div className={`mx-feature-panel is-layer-${activeLayer}`} id="mx-layer-panel" role="tabpanel" aria-labelledby={`mx-layer-tab-${activeLayer}`}>
            <div className="mx-feature-top">
              <div className="mx-feature-introduction">
                <span className="mx-feature-eyebrow">{featuredLayers[activeLayer].eyebrow}</span>
                <h3>{featuredLayers[activeLayer].title}</h3>
                <p>{featuredLayers[activeLayer].description}</p>
                <div className="mx-feature-introduction-footer">
                  <span className="mx-feature-status"><span aria-hidden="true" /> Core features implemented</span>
                  <a href={`/discovery?product=${activeLayer}`}>Explore a free pilot <span aria-hidden="true">→</span></a>
                </div>
              </div>

            </div>

            <div className="mx-feature-list-header">
              <div>
                <span className="mx-feature-eyebrow">CAPABILITIES</span>
                <h4>What's included in {activeLayer === "neo" ? "Neo" : "Morpheus"}</h4>
              </div>
              <span>Built for daily operations and multi-site teams</span>
            </div>
            <div className="mx-feature-groups">
              {featureGroups[activeLayer].map((group) => (
                <section className="mx-feature-group" key={group.title} aria-label={group.title}>
                  <div className="mx-feature-group-icon"><NodeIcon type={group.icon} /></div>
                  <h5>{group.title}</h5>
                  <p>{group.description}</p>
                  <ul>
                    {group.items.map((item) => (
                      <li key={item}><span className="mx-feature-check" aria-hidden="true">✓</span><span>{item}</span></li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
            <p className="mx-feature-disclaimer">{featuredLayers[activeLayer].disclaimer}</p>
          </div>

          <aside className="mx-free-pilot" aria-labelledby="mx-free-pilot-title">
            <div className="mx-free-pilot-copy">
              <span className="mx-free-pilot-eyebrow">30-DAY FREE PILOT · SELECTED BUSINESSES</span>
              <h3 id="mx-free-pilot-title">See what changes in <em>your</em> operation.</h3>
              <p>Start with one operational challenge. We'll help you get set up, agree on what success looks like, and review the results together.</p>
              <div className="mx-free-pilot-benefits" aria-label="Pilot benefits">
                <span>30 days</span><span>R0 pilot fee</span><span>Onboarding support</span><span>No obligation to continue</span>
              </div>
            </div>
            <div className="mx-free-pilot-action">
              <span>Start with {activeLayer === "neo" ? "Neo workforce" : "Morpheus inventory"} operations</span>
              <a href={`/discovery?product=${activeLayer}`}>Apply for a 30-day pilot <span aria-hidden="true">↗</span></a>
              <small>Opens our existing Discovery form. Pilot scope is agreed with each selected business.</small>
            </div>
          </aside>
        </div>
      </section>

      <section className="mx-pricing-shell" id="pricing" aria-labelledby="mx-pricing-title">
        <div className="mx-pricing-inner">
          <div className="mx-pricing-heading">
            <span className="mx-layers-eyebrow">PRICING</span>
            <h2 id="mx-pricing-title">Start free. <strong>Scale when you're ready.</strong></h2>
            <p>Pilot MatrixOS in one site of your choosing at no cost, then activate the layers you need when you are ready to go live.</p>
          </div>

          <div className="mx-pricing-grid">
            {pricingCards.map((card) => (
              <article key={card.id} className={`mx-pricing-card ${card.id === "combined" ? "is-highlighted" : ""}`}>
                <div className="mx-pricing-card-top">
                  <span className="mx-pricing-label">{card.label}</span>
                  {card.badge && <span className="mx-pricing-badge">{card.badge}</span>}
                </div>
                <div className={`mx-pricing-price ${card.id === "neo" ? "is-neo" : ""}`}>
                  {card.id === "neo" ? (
                    <><span>R699 or</span><span className="mx-pricing-neo-variable">R35 <small>× active staff</small></span></>
                  ) : card.price}
                </div>
                <div className="mx-pricing-meta">{card.meta}</div>
                {card.note && <div className="mx-pricing-note">{card.note}</div>}
                <ul className="mx-pricing-points">
                  {card.items.map((item) => (
                    <li key={item}><span aria-hidden="true">✓</span><span>{item}</span></li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="mx-pricing-cta">
            <a className="mx-home-hero-primary" href="/discovery">Start your 30-day pilot <span aria-hidden="true">→</span></a>
            <small>Or book a discovery to see how MatrixOS fits your operation.</small>
          </div>
        </div>
      </section>


      <section className="mx-section-three" id="integrations" aria-labelledby="mx-integrations-title">
        <div className="mx-s3-inner">
          <div className="mx-s3-systems-block">
            <div className="mx-s3-heading-row">
              <div>
                <span className="mx-integrations-eyebrow">BUILT TO WORK WITH THE SYSTEMS YOU ALREADY USE</span>
                <h2 id="mx-integrations-title">Keep your systems. Add the operational layer.</h2>
              </div>
              <p>
                MatrixOS is designed to consume operational data from the platforms already running your business — rather than forcing you to replace them.
              </p>
            </div>

            <div className="mx-s3-logo-row" aria-label="Systems MatrixOS is designed to work around">
              <div className="mx-s3-logo-item is-gaap"><span className="mx-s3-brand-logo"><img src="https://gaap.co.za/favicon.ico" alt="GAAP" /></span><strong>GAAP</strong></div>
              <div className="mx-s3-logo-item is-pilot"><span className="mx-s3-brand-logo mx-s3-pilot-logo" aria-hidden="true">P</span><strong>Pilot</strong></div>
              <div className="mx-s3-logo-item is-oracle"><span className="mx-s3-brand-logo is-wide"><img src="https://www.oracle.com/favicon.ico" alt="Oracle MICROS" /></span><strong>Oracle MICROS</strong></div>
              <div className="mx-s3-logo-item is-lightspeed"><span className="mx-s3-brand-logo"><img src="https://www.lightspeedhq.com/favicon.ico" alt="Lightspeed" /></span><strong>Lightspeed</strong></div>
              <div className="mx-s3-logo-item is-more"><span className="mx-s3-logo-mark">+</span><strong>More</strong></div>
            </div>

            <p className="mx-integrations-note">Integration availability depends on the capabilities and access provided by each external system.</p>
          </div>

          <div className="mx-vars-strip" aria-label="MatrixOS operational value model">
            <article>
              <span className="mx-vars-icon"><NodeIcon type="visibility" /></span>
              <div><strong>Visibility</strong><p>One view of what matters across every site, in real time.</p></div>
            </article>
            <article>
              <span className="mx-vars-icon"><NodeIcon type="accountability" /></span>
              <div><strong>Accountability</strong><p>Clear expectations, measured consistently and owned by the right people.</p></div>
            </article>
            <article>
              <span className="mx-vars-icon"><NodeIcon type="response" /></span>
              <div><strong>Response</strong><p>Spot issues sooner, route the next action and keep operations moving.</p></div>
            </article>
            <article>
              <span className="mx-vars-icon"><NodeIcon type="sites" /></span>
              <div><strong>Scale</strong><p>Standardise what works and extend it confidently across every location.</p></div>
            </article>
          </div>

          <div className="mx-s3-cta-strip">
            <div className="mx-s3-cta-brand"><img src={matrixMark} alt="" /></div>
            <div className="mx-s3-cta-copy">
              <strong>Bring your operation into one layer.</strong>
              <span>MatrixOS sits above your systems so your team can focus on leading, not chasing.</span>
            </div>
            <div className="mx-s3-cta-actions">
              <a className="mx-home-discovery" href="/discovery">Book a Discovery</a>
            </div>
          </div>
        </div>
      </section>

      <footer className="mx-home-footer" aria-label="MatrixOS footer">
        <div className="mx-home-footer-inner">
          <div className="mx-home-footer-brand">
            <div>
              <strong>MatrixOS</strong>
              <span>Operate with Clarity.</span>
              <small>Built in South Africa for multi-site operations.</small>
              <small>B-BBEE Level 1 Contributor · 135% B-BBEE procurement recognition.</small>
            </div>
          </div>

          <nav className="mx-home-footer-links" aria-label="Footer navigation">
            <a href="#story">Our story</a>
            <a href="/contact">Contact</a>
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
            <a href="https://www.linkedin.com/company/112805381/" target="_blank" rel="noreferrer">LinkedIn ↗</a>
          </nav>

          <div className="mx-home-footer-legal">
            <span>© 2026 MatrixOS. All rights reserved.</span>
          </div>
        </div>
      </footer>

      {activeNode && (
        <div className="mx-node-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setActiveNode(null); }}>
          <section className="mx-node-modal" role="dialog" aria-modal="true" aria-labelledby="mx-node-modal-title">
            <button type="button" className="mx-node-modal-close" aria-label="Close" onClick={() => setActiveNode(null)}>×</button>
            <div className="mx-node-modal-kicker">{nodeDetails[activeNode].side}</div>
            <div className="mx-node-modal-heading">
              <span className="mx-node-modal-icon"><NodeIcon type={nodeIcons[activeNode]} /></span>
              <div><h2 id="mx-node-modal-title">{nodeDetails[activeNode].title}</h2><span>{activeNode}</span></div>
            </div>
            <p>{nodeDetails[activeNode].description}</p>
            <div className="mx-node-modal-fit"><strong>How it fits in MatrixOS</strong><span>{nodeDetails[activeNode].fit}</span></div>
          </section>
        </div>
      )}
    </main>
  );
}
